<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import MultiSelect from '@/components/multi-select'
import ListingPagination from '@/components/listing-pagination'
import i18n from '@/i18n'
import { mapState } from 'vuex'
import { SESSION, SHARED } from '@/constants'
import { sessionHelper, ddlHelper } from '@/utils'
import { ROLE_MODULE } from '@/state/modules/role'
import { roleMethods } from '@/state/helpers'

export default {
  page: { title: i18n.t('label.listingX', [i18n.t('module.role')]) },
  components: { Layout, PageHeader, MultiSelect, ListingPagination },
  data() {
    return {
      title: i18n.t('label.listingX', [i18n.t('module.role')]),
      breadcrumbItems: [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.role'), active: true },
      ],
      statusDdl: ddlHelper.getActivationStatusMulti(),
      filter: '',
      searchCriteria: {},
      showFilters: false,
      fields: [
        { key: 'name', sortable: true, label: i18n.t('label.name') },
        { key: 'permissions', sortable: false, label: i18n.t('label.permissions') },
        { key: 'isActive', sortable: false, label: i18n.t('label.status') },
        { key: 'action', sortable: false, label: i18n.t('label.action') },
      ],
    }
  },
  computed: {
    ...mapState(ROLE_MODULE, {
      tableRows: (state) => state.list.data,
      pagination: (state) => state.list.pagination,
      status: (state) => state.status,
      permissionGroups: (state) => state.permissions,
    }),
    /** Flat list of every right, for the "which roles grant this" filter. */
    permissionDdl() {
      return this.permissionGroups.reduce(
        (all, group) =>
          all.concat((group.permissions || []).map((p) => ({ text: p.name, value: p.code }))),
        []
      )
    },
    activeFilterCount() {
      const c = this.searchCriteria
      return [c.isActive, c.permissionCodes].filter((v) => Array.isArray(v) && v.length > 0).length
    },
  },
  created() {
    this.initSearchCriteria()
    this.lookupPermission()
  },
  methods: {
    ...roleMethods,

    initSearchCriteria() {
      if (sessionHelper.hasSearchCriteria(SESSION.ROLES)) {
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.ROLES)
        this.filter = (this.searchCriteria.searchArray || [])[0] || ''
        this.showFilters = this.activeFilterCount > 0
      } else {
        this.searchCriteria = this.defaultCriteria()
      }

      this.getList(this.searchCriteria)
    },

    defaultCriteria() {
      return {
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        pageSize: SHARED.DEFAULT_PAGE_SIZE,
        searchArray: [],
        isActive: [],
        permissionCodes: [],
      }
    },

    getList(filter) {
      this.getAllRole({ filter })
      this.searchCriteria = filter
      sessionHelper.storeSearch(SESSION.ROLES, filter)
    },

    pageNavigation(pageNumber) {
      this.getList({ ...this.searchCriteria, pageNumber })
    },

    searchListing() {
      this.getList({
        ...this.searchCriteria,
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        searchArray: this.filter ? [this.filter] : [],
      })
    },

    resetFilters() {
      this.filter = ''
      this.getList(this.defaultCriteria())
    },

    toggleStatus(row) {
      this.updateRoleStatus({
        id: row.id,
        isActive: !row.isActive,
        filter: this.searchCriteria,
      })
    },

    goCreate() {
      this.$router.push({ name: 'Role-create' })
    },

    goEdit(row) {
      this.$router.push({ name: 'Role-update', params: { id: row.id } })
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <!-- Roles are platform-wide — there is no tenant_id on the table —
             so a supervisor editing one edits every brand's. Said here rather
             than discovered. -->
        <b-alert show variant="warning" class="small d-flex align-items-start">
          <i class="mdi mdi-alert-outline font-size-16 mr-2"></i>
          <span>{{ $t('role.platformWideNotice') }}</span>
        </b-alert>

        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center w-100">
                  {{ $t('label.search') }}
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('label.search')"
                    class="form-control form-control-sm ml-2"
                    @change="searchListing"
                  ></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button variant="outline-secondary" size="sm" class="mr-2" @click="showFilters = !showFilters">
                  <i class="mdi mdi-filter-variant mr-1"></i>{{ $t('label.filter') }}
                  <b-badge v-if="activeFilterCount" variant="primary" class="ml-1">{{ activeFilterCount }}</b-badge>
                </b-button>
                <b-button v-if="$can('Create Role')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.role')]) }}
                </b-button>
              </div>
            </div>

            <b-collapse v-model="showFilters">
              <div class="border rounded p-3 mb-3 bg-light">
                <b-row>
                  <b-col cols="12" md="4" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('label.status') }}</label>
                    <MultiSelect v-model="searchCriteria.isActive" :options="statusDdl" @change="searchListing" />
                  </b-col>
                  <b-col cols="12" md="8" class="mb-2">
                    <label class="small font-weight-bold mb-1">{{ $t('role.grantsPermission') }}</label>
                    <MultiSelect
                      v-model="searchCriteria.permissionCodes"
                      :options="permissionDdl"
                      @change="searchListing"
                    />
                  </b-col>
                  <b-col cols="12" class="text-right">
                    <b-button variant="link" size="sm" @click="resetFilters">
                      {{ $t('label.clearFilters') }}
                    </b-button>
                  </b-col>
                </b-row>
              </div>
            </b-collapse>

            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :busy="!status.list" show-empty>
                <template #empty>
                  <p class="text-muted text-center my-3">{{ $t('label.noRecordFound') }}</p>
                </template>

                <template #cell(permissions)="data">
                  <!-- The count, not the list: an Administrator role has
                       fourteen badges and the table becomes unreadable. -->
                  <span class="small">
                    {{ $t('role.nPermissions', [data.item.permissionCount]) }}
                  </span>
                </template>

                <template #cell(isActive)="data">
                  <b-badge :variant="data.item.isActive ? 'success' : 'secondary'">
                    {{ data.item.isActive ? $t('label.active') : $t('label.inactive') }}
                  </b-badge>
                </template>

                <template #cell(action)="data">
                  <a
                    v-if="$can('Update Role')"
                    href="javascript:void(0);"
                    class="text-info mr-2"
                    v-b-tooltip.hover
                    :title="$t('label.updateX', [$t('module.role')])"
                    @click="goEdit(data.item)"
                  >
                    <i class="mdi mdi-pencil font-size-18"></i>
                  </a>
                  <a
                    v-if="$can('Update Role')"
                    href="javascript:void(0);"
                    class="text-warning"
                    v-b-tooltip.hover
                    :title="$t('label.status')"
                    @click="toggleStatus(data.item)"
                  >
                    <i class="mdi mdi-power font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>

            <ListingPagination :pagination="pagination" @change="pageNavigation" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
